import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

import LoginViewport from '../components/LoginViewport';
import SvgLoading from '../components/SvgLoading';
import HttpClient from '../utils/HttpClient';
import { validatePassword } from '../utils/validation';


export default function ResetPassword() {
  const cookies = new Cookies(null, { path: '/' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: '',
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingKey, setIsValidatingKey] = useState(true);

  const resetKey = useMemo(() => searchParams.get('key'), [searchParams]);

  const isFormValid = useMemo(() => {
    return Boolean(
      formData.code.trim() &&
      formData.password &&
      formData.confirmPassword
    );
  }, [formData.code, formData.password, formData.confirmPassword]);

  const handleInputChange = useCallback((field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  }, []);

  useEffect(() => {
    if (!resetKey) {
      toast.error(t('Login:Invalid reset link'));
      navigate('/login', { replace: true });
      return;
    }

    const validateResetKey = async () => {
      try {
        const response = await HttpClient.get(`/reset/?key=${resetKey}`);

        if (!response?.valid) {
          toast.error(t('Login:Invalid or expired reset link'));
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Error validating reset key:', error);
        toast.error(t('Common:Server Error'));
        navigate('/login', { replace: true });
      } finally {
        setIsValidatingKey(false);
      }
    };

    validateResetKey();
  }, [resetKey, navigate, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!isFormValid) return;
    setIsSubmitting(true);

    try {
      const trimmedCode = formData.code.trim();
      if (!validatePassword(formData.password, formData.confirmPassword)) {
        setIsSubmitting(false);
        return;
      }

      const payload = {
        code: trimmedCode,
        password: formData.password,
      };
      const response = await HttpClient.post(
        `/reset/?key=${resetKey}`,
        payload,
        cookies.get('token')
      );

      if (response.ok && response.status === 200) {
        toast.success(t('Login:Password reset successfully'));
        navigate('/login', { replace: true });
      } else {
        toast.error(t('Common:Server Error'));
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(t('Common:Server Error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isFormValid, resetKey, navigate, t, cookies]);

  const FormInput = useCallback(({ id, label, type = 'text', value, onChange }) => (
    <div className="mt-5">
      <label htmlFor={id} className="block text-sm font-medium leading-6 text-white mb-2">
        {label}
      </label>

      <div className="relative">
        <input
          type={type}
          name={id}
          id={id}
          value={value}
          className="block w-full rounded-xl border-0 py-3.5 px-4 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 transition-all duration-200"
          onChange={onChange}
          disabled={isSubmitting}
          required
          autoComplete={type === 'password' ? 'new-password' : 'off'}
        />
      </div>
    </div>
  ), [isSubmitting]);

  if (isValidatingKey) {
    return (
      <LoginViewport>
        <div className="flex flex-col items-center justify-center">
          <SvgLoading />
          <p className="text-slate-400 mt-4">{t('Common:Loading')}</p>
        </div>
      </LoginViewport>
    );
  }

  return (
    <LoginViewport>
      <div className="w-full">
        <div className="lg:hidden flex justify-center mb-10">
          <img
            className="h-12 w-auto"
            src="/public/assets/logo-harpiAI.svg"
            alt="HarpiAI"
          />
        </div>

        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            {t('Login:Reset password')}
          </h2>
          <p className="mt-2 text-md text-white">
            {t('Login:Input code and reset your password')}
          </p>
        </div>

        <div className="mt-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-6">
              <FormInput
                id="code"
                label={t('Login:Code')}
                type="text"
                value={formData.code}
                onChange={handleInputChange('code')}
              />

              <FormInput
                id="password"
                label={t('Login:Password')}
                type="password"
                value={formData.password}
                onChange={handleInputChange('password')}
              />

              <FormInput
                id="confirmPassword"
                label={t('Login:Confirm Password')}
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
              />
            </div>

            <div className="mt-8">
              <button
                disabled={isSubmitting || !isFormValid}
                type="submit"
                className="flex w-full justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3.5 text-sm font-semibold text-white shadow-md hover:from-blue-500 hover:to-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform active:scale-[0.98]"
              >
                {isSubmitting ? <SvgLoading /> : t('Login:Submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </LoginViewport>
  );
};
